import { Application } from "@hotwired/stimulus";
import { UltimateTurboModalController as BaseModalController } from "ultimate_turbo_modal";
import { identifierForContextKey } from "stimulus/webpack-helpers";
import { controllerDefinitions as bulletTrainControllers } from "@bullet-train/bullet-train";
import { controllerDefinitions as bulletTrainFieldControllers } from "@bullet-train/fields";
import { controllerDefinitions as bulletTrainSortableControllers } from "@bullet-train/bullet-train-sortable";
import ScrollReveal from "stimulus-scroll-reveal";
import RevealController from "stimulus-reveal";
import CableReady from "cable_ready";
import consumer from "../channels/consumer";
import { requestPageRefresh } from "../support/turbo_custom_actions";

const application = Application.start();
const parentRefreshUrls = new WeakMap();

class ModalController extends BaseModalController {
  connect() {
    this.parentRefreshUrl = null;
    this.returnedParentUrl = null;
    this.pendingParentChanges = 0;
    this.parentChangeDidFail = false;
    this.closeWhenParentChangesSettle = false;
    this.bypassParentChangeWait = false;
    this.deferredModalAction = null;
    this.suppressOutsideModalClickForOpenSelect2 = false;
    this.clearSelect2OutsideClickSuppression = null;
    this.recordSelect2OutsidePointerDown = this.recordSelect2OutsidePointerDown.bind(this);
    this.deferParentChangingClick = this.deferParentChangingClick.bind(this);
    this.deferParentChangingSubmit = this.deferParentChangingSubmit.bind(this);
    this.refreshChangedParentAfterPopstate = this.refreshChangedParentAfterPopstate.bind(this);

    document.addEventListener("mousedown", this.recordSelect2OutsidePointerDown, true);
    document.addEventListener("touchstart", this.recordSelect2OutsidePointerDown, true);

    this.recordDestructiveLinkClick = this.recordDestructiveLinkClick.bind(this);
    this.closeOnDestructiveSubmitEnd = this.closeOnDestructiveSubmitEnd.bind(this);

    // v3 adds a bubbling popstate handler in connect() which resets the
    // history-advanced flag and tears the dialog down immediately. Capture lets
    // this handler read the flag first; registering before super.connect() keeps
    // that ordering if the library ever switches its listener to capture too.
    // Going first restores the intent the handler was written with: take the
    // Back action, then decide whether the current DOM can be retained or needs
    // a morph.
    window.addEventListener("popstate", this.refreshChangedParentAfterPopstate, {capture: true});

    super.connect();
    this.parentRefreshUrl = parentRefreshUrls.get(this.turboFrame) || null;
    this.turboFrame.addEventListener("click", this.deferParentChangingClick, true);
    this.turboFrame.addEventListener("submit", this.deferParentChangingSubmit, true);
    this.turboFrame.addEventListener("click", this.recordDestructiveLinkClick, true);
    document.addEventListener("turbo:submit-end", this.closeOnDestructiveSubmitEnd, true);
  }

  disconnect() {
    document.removeEventListener("mousedown", this.recordSelect2OutsidePointerDown, true);
    document.removeEventListener("touchstart", this.recordSelect2OutsidePointerDown, true);
    this.turboFrame?.removeEventListener("click", this.deferParentChangingClick, true);
    this.turboFrame?.removeEventListener("submit", this.deferParentChangingSubmit, true);
    this.turboFrame?.removeEventListener("click", this.recordDestructiveLinkClick, true);
    document.removeEventListener("turbo:submit-end", this.closeOnDestructiveSubmitEnd, true);
    window.removeEventListener("popstate", this.refreshChangedParentAfterPopstate, {capture: true});

    if (this.clearSelect2OutsideClickSuppression) {
      clearTimeout(this.clearSelect2OutsideClickSuppression);
    }

    super.disconnect();
  }

  // v3 renamed this outsideModalClicked -> dialogClicked and paired it with
  // dialogMousedown, which records whether the press began inside the content
  // (this._mousedownInsideContent). Ours still runs first: select2 can leave a
  // click resolving outside the content even when the press began on one of its
  // options, and returning early has to clear v3's flag or it leaks into the
  // next click.
  dialogClicked(event) {
    if (this.suppressOutsideModalClickForOpenSelect2) {
      this._mousedownInsideContent = false;
      if (!this.clearSelect2OutsideClickSuppression) {
        this.clearSelect2OutsideClickSuppression = setTimeout(() => {
          this.suppressOutsideModalClickForOpenSelect2 = false;
          this.clearSelect2OutsideClickSuppression = null;
        }, 0);
      }

      return;
    }

    super.dialogClicked(event);
  }

  cancel(event) {
    event.preventDefault();
    event.stopPropagation();
    this.hidePreservingPage();
  }

  parentChangeStarted(event) {
    if (!this.parentRefreshUrlFor(event)) return;

    if (this.pendingParentChanges === 0) this.parentChangeDidFail = false;
    this.pendingParentChanges += 1;
  }

  markParentChanged(event) {
    const refreshUrl = this.parentRefreshUrlFor(event);
    if (!refreshUrl) return;

    this.setParentRefreshUrl(this.returnedParentUrl || refreshUrl);
  }

  preventParentChangeEscalation(event) {
    if (this.closeWhenParentChangesSettle) event.preventDefault();
  }

  parentChangeSettled(event) {
    if (!this.parentRefreshUrlFor(event)) return;

    if (!event.detail.saved) this.parentChangeDidFail = true;
    this.pendingParentChanges = Math.max(0, this.pendingParentChanges - 1);
    if (this.pendingParentChanges > 0) return;

    if (!this.closeWhenParentChangesSettle) {
      this.replayDeferredModalAction();
      return;
    }

    this.deferredModalAction = null;
    this.closeWhenParentChangesSettle = false;
    this.hideModal();
  }

  deferParentChangingClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest("button[type='submit'], input[type='submit'], a[data-turbo-method]");
    if (!trigger || !this.turboFrame.contains(trigger)) return;

    const activeEdit = this.activeParentEdit();
    if (!activeEdit && this.pendingParentChanges === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.deferredModalAction = () => trigger.click();

    if (!activeEdit || activeEdit.dataset.inlineEditSavingValue === "true") return;

    activeEdit.dispatchEvent(new CustomEvent("modal:finish-pending-edit"));
    if (this.pendingParentChanges > 0) return;

    const editFinishedWithoutRequest = activeEdit.dataset.inlineEditEditingValue !== "true";
    if (editFinishedWithoutRequest) {
      this.replayDeferredModalAction();
    } else {
      this.deferredModalAction = null;
    }
  }

  deferParentChangingSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    // A nested inline edit has already changed the parent canvas. Tell the
    // main save so a comment-only response cannot discard its pending refresh.
    if (this.parentRefreshUrl && !form.querySelector("input[name='modal_parent_changed']")) {
      const marker = document.createElement("input");
      marker.type = "hidden";
      marker.name = "modal_parent_changed";
      marker.value = "true";
      form.append(marker);
    }
    if (this.pendingParentChanges === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter;
    this.deferredModalAction = () => {
      if (submitter) {
        form.requestSubmit(submitter);
      } else {
        form.requestSubmit();
      }
    };
  }

  hideModal(eventOrOptions) {
    // v3 changed this signature from hideModal(event) to
    // hideModal({skipHistoryBack}). Both reach us: the close button's
    // data-action="modal#hideModal" hands Stimulus an Event, while UTM's own
    // hide()/close()/cancelEvent call it with nothing or with options. Sort
    // them out once, and forward the options so a caller asking to keep the
    // history entry still gets that from super.
    const options = eventOrOptions instanceof Event || !eventOrOptions ? {} : eventOrOptions;

    if (this.bypassParentChangeWait) {
      // Through the history-consuming close, not super.hideModal() directly.
      //
      // This branch is how stream dismissals and every successful modal
      // submit close -- saves and removes -- because submitEnd sets the
      // bypass so the close does not wait on parent-change bookkeeping the
      // turbo-stream response has already settled. Raw super.hideModal()
      // ends in history.back() with Turbo's history listener attached, and
      // our popstate interceptor deliberately stands down while hidingModal
      // is set -- leaving Turbo free to run a restoration visit over a page
      // the streams just updated. hideModalPreservingPage consumes the
      // history entry with Turbo's listener stopped, exactly as the
      // non-bypass paths already do, so the URL returns to the canvas and
      // the DOM stays put.
      //
      // (The visible post-close repaint recorded on #destroy turned out to
      // come from the action's own delayed turbo_refresh_action_tag, not
      // from a restoration visit -- and it morphs. This branch is
      // consistency and defence, not the flicker fix; the flicker fixes are
      // the ultimate_turbo_modal src-strip patch and the modal-frame morph
      // guard in turbo_custom_actions.js.)
      this.hideModalPreservingPage(options);
      return;
    }

    if (eventOrOptions instanceof Event) eventOrOptions.stopPropagation();
    this.cancelPendingParentEdits();

    if (this.pendingParentChanges > 0) {
      this.closeWhenParentChangesSettle = true;
      return;
    }

    if (!this.parentRefreshUrl) {
      this.hideModalPreservingPage(options);
      return;
    }

    if (this.hidingModal) {
      super.hideModal(options);
      return;
    }

    this.hideChangedParent(options);
  }

  hide() {
    // Stream-driven dismissals call hide(); passive close controls call
    // hideModal() and should retain a pending parent refresh.
    this.setParentRefreshUrl(null);
    this.closeWhenParentChangesSettle = false;
    this.deferredModalAction = null;
    this.bypassParentChangeWait = true;
    try {
      super.hide();
    } finally {
      this.bypassParentChangeWait = false;
    }
  }

  submitEnd(event) {
    if (!event.detail.success) {
      super.submitEnd(event);
      return;
    }

    this.setParentRefreshUrl(null);
    this.closeWhenParentChangesSettle = false;
    this.deferredModalAction = null;
    this.bypassParentChangeWait = true;
    try {
      // Deliberately not super.submitEnd(). v3 added a smooth-redirect path:
      // when the response was a redirect it records the URL and does NOT
      // close, leaving the close to turbo:frame-missing -- which morphs the
      // page behind the modal and then closes it. That never fires here.
      // Every one of our layouts renders an empty <turbo-frame id="modal">,
      // so the redirect target always contains the frame, the response is
      // never "missing" it, and the modal is simply left open. Recorded on
      // the planning-value create, which redirects with :see_other.
      //
      // Closing on every successful submit is what v2 did and what the rest
      // of this controller is built around: the parent refresh is ours to
      // drive (parentRefreshUrl / refreshChangedParent), not Turbo's.
      this._pendingRedirectUrl = null;
      this.hideModal();
    } finally {
      this.bypassParentChangeWait = false;
    }
  }

  refreshChangedParentAfterPopstate(event) {
    if (this.hidingModal) return;
    if (document.body.getAttribute("data-turbo-modal-history-advanced") !== "true") return;

    // Turbo registered its restoration listener before the modal. Capture the
    // Back action first so it cannot render a stale cached parent before the
    // modal decides whether the current DOM can be retained or needs a morph.
    event.stopImmediatePropagation();
    this.returnedParentUrl = window.location.href;
    if (this.parentRefreshUrl) this.setParentRefreshUrl(this.returnedParentUrl);
    document.body.removeAttribute("data-turbo-modal-history-advanced");
    this.hideModal();
  }

  refreshChangedParent(refreshUrl) {
    window.history.replaceState(window.history.state, "", refreshUrl);
    document.body.removeAttribute("data-turbo-modal-history-advanced");
    this.setParentRefreshUrl(null);
    requestPageRefresh(refreshUrl, {preserveScroll: true});
  }

  setParentRefreshUrl(refreshUrl) {
    this.parentRefreshUrl = refreshUrl;
    if (!this.turboFrame) return;

    if (refreshUrl) {
      parentRefreshUrls.set(this.turboFrame, refreshUrl);
    } else {
      parentRefreshUrls.delete(this.turboFrame);
    }
  }

  parentRefreshUrlFor(event) {
    const parentRefreshUrl = event.currentTarget.dataset.modalParentRefreshUrl;
    if (!parentRefreshUrl) return null;

    const refreshUrl = new URL(parentRefreshUrl, window.location.origin);
    return refreshUrl.origin === window.location.origin ? refreshUrl.href : null;
  }

  cancelPendingParentEdits() {
    this.turboFrame.querySelectorAll(
      "[data-modal-parent-refresh-url][data-inline-edit-editing-value='true'][data-inline-edit-saving-value='false']"
    ).forEach((element) => {
      element.dispatchEvent(new CustomEvent("modal:cancel-pending-edit"));
    });
  }

  activeParentEdit() {
    return this.turboFrame.querySelector(
      "[data-modal-parent-refresh-url][data-inline-edit-editing-value='true']"
    );
  }

  replayDeferredModalAction() {
    const deferredModalAction = this.deferredModalAction;
    const parentChangeDidFail = this.parentChangeDidFail;
    this.deferredModalAction = null;
    this.parentChangeDidFail = false;
    if (!deferredModalAction || parentChangeDidFail) return;

    window.setTimeout(deferredModalAction, 0);
  }

  hidePreservingPage() {
    this.hideModal();
  }

  hideChangedParent(options = {}) {
    const refreshUrl = this.parentRefreshUrl;
    if (document.body.getAttribute("data-turbo-modal-history-advanced") !== "true") {
      // Called directly, not off modal:closed. v2 dispatched that event
      // synchronously from hideModal, so this ran immediately -- while the
      // modal was still animating out. v3 dispatches it from the close
      // cleanup 200-300ms later, and cancels that cleanup if the controller
      // disconnects first.
      //
      // On the Back path those two facts combine badly: our popstate handler
      // cannot stop Turbo's, which was registered when Turbo booted and so
      // runs first whatever our capture flag says. v2 got away with it by
      // winning the race -- its refresh visit started synchronously inside
      // super.hideModal(), before Turbo's restoration could render. Under v3
      // the restoration lands at ~410ms, replaces the body, disconnects the
      // controller, cancels the cleanup, and modal:closed never fires at all,
      // so the parent is never refreshed. Traced on the shared plan-job
      // rename: modal:closing@300, then turbo:load@431, and no modal:closed.
      super.hideModal(options);
      if (this.hidingModal) {
        this.refreshChangedParent(this.returnedParentUrl || refreshUrl);
      }
      return;
    }

    // Consume the modal's pushed history entry before refreshing its parent.
    // Otherwise replacing the edit URL with the parent URL leaves two adjacent
    // parent entries, so the user's next Back can restore a stale canvas.
    const turboHistory = window.Turbo?.session?.history;
    const restartTurboHistory = turboHistory?.started === true;
    if (restartTurboHistory) turboHistory.stop();

    let returnTimeout;
    const refreshParentAfterReturn = (event) => {
      event.stopImmediatePropagation();
      clearTimeout(returnTimeout);
      document.body.removeAttribute("data-turbo-modal-history-advanced");
      if (restartTurboHistory) turboHistory.start();
      this.refreshChangedParent(window.location.href);
    };

    window.addEventListener("popstate", refreshParentAfterReturn, {once: true, capture: true});
    returnTimeout = window.setTimeout(() => {
      window.removeEventListener("popstate", refreshParentAfterReturn, {capture: true});
      if (restartTurboHistory) turboHistory.start();
      this.refreshChangedParent(refreshUrl);
    }, 1000);

    super.hideModal(options);

    if (!this.hidingModal) {
      clearTimeout(returnTimeout);
      window.removeEventListener("popstate", refreshParentAfterReturn, {capture: true});
      if (restartTurboHistory) turboHistory.start();
    }
  }

  hideModalPreservingPage(options = {}) {
    if (document.body.getAttribute("data-turbo-modal-history-advanced") !== "true") {
      super.hideModal(options);
      return;
    }

    // The modal's history.back() should update the URL without asking Turbo to
    // restore its cached copy of the parent page over the still-current DOM.
    const turboHistory = window.Turbo?.session?.history;
    if (!turboHistory?.started) {
      super.hideModal(options);
      return;
    }

    turboHistory.stop();

    let resumeTimeout;
    const resumeTurboHistory = () => {
      clearTimeout(resumeTimeout);
      window.setTimeout(() => turboHistory.start(), 0);
    };

    window.addEventListener("popstate", resumeTurboHistory, {once: true});
    resumeTimeout = window.setTimeout(() => {
      window.removeEventListener("popstate", resumeTurboHistory);
      turboHistory.start();
    }, 1000);

    super.hideModal(options);
  }

  // A destroy launched from inside the modal must close the modal when it
  // succeeds -- and cannot do so through any of UTM's own paths. The Remove
  // controls are links with data-turbo-method, and Turbo submits those through
  // a synthetic form appended to document.body: turbo:submit-end fires OUTSIDE
  // the frame, so UTM's own submitEnd (scoped to the container) never hears
  // it. Until the morph guard, these dialogs were being closed by the
  // response's page refresh morphing an empty modal frame over them -- closure
  // as a side effect of the bug this branch fixes. One controller
  // (assigned_jobs#destroy) closes properly by rendering a dismiss_modal
  // stream; the rest never did. This is that dismissal, derived client-side,
  // for every destructive link the modal contains.
  //
  // Scope: DELETE links only, and any that keep their dialog open must say so
  // with data-modal-keep-open. The first audit claimed every turbo-method link
  // inside a modal was a leave-taking destroy; the overview-view share dialog
  // disproved it -- its "Revoke link" is a DELETE whose success re-renders the
  // dialog it lives in, and it sits next to a turbo_method: :post regenerate
  // control. Method filtering excludes the POSTs; the attribute carries the
  // rest. Where the server also sends dismiss_modal, the second close is a
  // no-op behind UTM's hidingModal guard.
  recordDestructiveLinkClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest("a[data-turbo-method='delete']");
    if (!link || !this.turboFrame.contains(link)) return;
    if (link.hasAttribute("data-modal-keep-open")) return;

    this.pendingDestructiveHref = new URL(link.getAttribute("href"), window.location.origin).pathname;
  }

  closeOnDestructiveSubmitEnd(event) {
    if (!this.pendingDestructiveHref) return;
    if (!event.detail?.success) return;

    // Only submissions from OUTSIDE the frame qualify. Turbo submits a
    // data-turbo-method link through a synthetic form appended to
    // document.body -- that is the destroy this record was made for. A form
    // INSIDE the frame posting to the same resource path is something else
    // entirely: the edit modal's PATCH goes to the very URL the Remove link
    // deletes, so a cancelled confirm followed by a successful save would
    // otherwise close a modal the user is still working in.
    const form = event.target;
    if (form instanceof Element && this.turboFrame?.contains(form)) return;

    const location = event.detail.formSubmission?.location;
    if (!location || location.pathname !== this.pendingDestructiveHref) return;

    this.pendingDestructiveHref = null;
    this.hide();
  }

  recordSelect2OutsidePointerDown(event) {
    if (!document.querySelector(".select2-container--open")) return;
    if (!this.hasContentTarget) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (this.contentTarget.contains(target) || this.contentTarget === target) return;

    this.suppressOutsideModalClickForOpenSelect2 = true;
  }
}

// In the browser console:
// * Type `window.Stimulus.debug = true` to log actions and lifecycle hooks
//   on subsequent user interactions and Turbo page views.
// * Type `window.Stimulus.router.modulesByIdentifier` for a list of loaded controllers.
// See https://stimulus.hotwired.dev/handbook/installing#debugging
window.Stimulus = application;

// Load all the controllers within this directory and all subdirectories.
// Controller files must be named *_controller.js.
import { context as controllersContext } from "./**/*_controller.js";

application.register("reveal", RevealController);
application.register("scroll-reveal", ScrollReveal);
application.register("modal", ModalController);

let controllers = Object.keys(controllersContext).map((filename) => ({
  identifier: identifierForContextKey(filename),
  controllerConstructor: controllersContext[filename],
}));

controllers = overrideByIdentifier([
  ...bulletTrainControllers,
  ...bulletTrainFieldControllers,
  ...bulletTrainSortableControllers,
  ...controllers,
]);

application.load(controllers);

CableReady.initialize({ consumer });

function overrideByIdentifier(controllers) {
  const byIdentifier = {};

  controllers.forEach((item) => {
    byIdentifier[item.identifier] = item;
  });

  return Object.values(byIdentifier);
}
